import React from 'react';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  errorMessage?: string;
}

class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return {
      hasError: true,
      errorMessage: error.message,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Client app crashed:', error, errorInfo);
    // Best-effort report to backend. Fire-and-forget; even if it fails
    // we still render the fallback UI.
    try {
      fetch('/api/v1/errors/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          app: 'client',
          url: window.location.href,
          message: error.message || 'Unknown error',
          stack: (error.stack || '') + (errorInfo.componentStack ? '\n--- React component stack ---\n' + errorInfo.componentStack : ''),
          userAgent: navigator.userAgent,
        }),
      }).catch(() => {});
    } catch {
      // ignore
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-bg-primary px-4 text-center">
          <div className="w-full max-w-lg rounded-2xl border border-line bg-bg-card p-8 shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
            <h1 className="text-2xl font-bold text-fg-primary">Сайт временно перезапускается</h1>
            <p className="mt-3 text-sm leading-6 text-zinc-400">
              Мы поймали ошибку на странице и не дали приложению уйти в пустой экран.
            </p>
            {this.state.errorMessage && (
              <p className="mt-3 rounded-lg border border-line bg-black/20 px-4 py-3 text-xs text-zinc-500">
                {this.state.errorMessage}
              </p>
            )}
            <button
              type="button"
              onClick={this.handleReload}
              className="mt-6 inline-flex min-h-[44px] items-center justify-center rounded-lg bg-white px-5 py-3 text-sm font-semibold text-black transition hover:opacity-90"
            >
              Перезагрузить страницу
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
